import { useState } from "react";
import { ModalComponent } from "..";
import config from "../../../config";

/**
 * ImageElement component for rendering images based on the provided source or default image.
 * Includes a modal for full-size image preview when clicked.
 * @param {Object} props - Component properties.
 * @param {string} props.previewSource - Preview image source.
 * @param {string} props.source - Image source.
 * @param {string} props.imageFor - Type of image (e.g., "user" or "admin").
 * @param {string} props.alt - Alternative text for the image.
 * @param {string} props.origin - Cross-origin attribute for the image.
 * @param {boolean} props.disableModal - Whether to disable modal functionality (default: false).
 * @param {Object} props.rest - Additional props for the image element.
 * @returns {JSX.Element} - Rendered ImageElement component with optional modal functionality.
 */
function ImageElement({
  previewSource = "",
  source,
  imageFor = "user",
  alt = "image",
  origin = "anonymous",
  bigImageSourse,
  disableModal = false,
  ...rest
}) {
  // State to control modal visibility
  const [showModal, setShowModal] = useState(false);

  // Define image paths based on imageFor type
  const imagePath = {
    user: config.IMAGE_URL,
    admin: config.ADMIN_IMAGE_URL,
  };

  // Determine the current image source to use in both preview and modal
  const currentImageSrc =
    bigImageSourse || previewSource ||
    (source
      ? `${imagePath[imageFor]}/${source}`
      : `${imagePath[imageFor]}/profile-img.jpg`);

  // Handle image click to open modal
  const handleImageClick = () => {
    setShowModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      {/* Regular image that opens modal when clicked (if modal is enabled) */}
      {previewSource ? (
        <img
          src={previewSource}
          alt={alt}
          onClick={!disableModal ? handleImageClick : undefined}
          style={!disableModal ? { cursor: "pointer" } : {}}
          {...rest}
        />
      ) : (
        <>
          {source ? (
            <img
              src={`${imagePath[imageFor]}/${source}`}
              crossOrigin={origin}
              alt={alt}
              onClick={!disableModal ? handleImageClick : undefined}
              style={!disableModal ? { cursor: "pointer" } : {}}
              {...rest}
            />
          ) : (
            <img
              src={`${imagePath[imageFor]}/profile-img.jpg`}
              alt={alt || "userImage"}
              onClick={!disableModal ? handleImageClick : undefined}
              style={!disableModal ? { cursor: "pointer" } : {}}
            />
          )}
        </>
      )}

      {/* Bootstrap Modal for image preview (only render if modal is enabled) */}
      {!disableModal && (
        <ModalComponent
          show={showModal}
          onHandleCancel={handleCloseModal}
          backdrop="static"
          modalExtraClass="image-preview-modal"
          extraClassName="modal-dialog-centered modal-lg"
          closeButton
          title={alt}
        >
          <div className="text-center">
            <img
              src={currentImageSrc}
              alt={alt}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          </div>
        </ModalComponent>
      )}
    </>
  );
}

export default ImageElement;
